import {useCallback, useMemo} from 'react'

import {cellHasUnloadedItems} from '../cells/cell-has-unloaded-items'
import {useBoardFocusGrid, useBoardFocusStore} from '../focus/contexts'
import {composeCellKey} from '../provider/cell-key'
import {
  useBoardCanMoveCards,
  useBoardCardDragPolicy,
  useBoardCellOrdering,
  useBoardConfig,
  useBoardDataContext,
  useBoardItemDropPolicy,
  useBoardItemsMoveReport,
} from '../provider/contexts'
import type {CellAddress, CellLabelAddress, ItemId} from '../types'
import {type BoardCardMoveAxis, type BoardCardMoveStep, cardMoveAxes, cardMoveOptions} from './card-moves'

/** One direction a card can be moved in from its menu. */
export type BoardCardMove = {
  /**
   * `card` is up and down, which follow the board's reading order: among the card's
   * neighbors in a cell the caller ranks by hand, and to the swimlane above or below past
   * either end of one and anywhere in a sorted cell. `column` is left and right.
   */
  axis: BoardCardMoveAxis
  /**
   * Toward the start of the axis or toward its end. On the column axis that is the
   * writing direction, so `previous` is to the right on a right-to-left board.
   */
  step: BoardCardMoveStep
  /**
   * Whether the move can be made now. `false` at the edge of the board, past cards or
   * columns that have not loaded, in a cell the caller's `canDropItem` refuses, and for a
   * card the caller refuses to move at all.
   */
  isAvailable: boolean
  /**
   * The cell the card lands in, in the words a reader hears, so a menu can say where a
   * move goes. Absent for a reorder inside the card's own cell and for a direction with no
   * cell that way. `rowLabel` is absent on a board whose rows have no visible name.
   */
  destination?: CellLabelAddress
}

export type UseBoardCardMovesResult = {
  /**
   * Up and down, then left and right. A direction the board can never move the card in is
   * left out: up and down on a board with one swimlane and no hand-ranked cell here, and
   * left and right on a board with one column. Empty on a board with no `onItemsMove`.
   */
  moves: readonly BoardCardMove[]
  /** Why the caller refuses to let this card move, from `getItemDragState`, or absent. */
  refusal?: string
  /**
   * Reports one of {@link UseBoardCardMovesResult.moves} through `onItemsMove`, exactly as
   * a drop reports one. Does nothing for a move that is not available.
   *
   * Focus goes with the card: once the caller hands back data with the card where it was
   * sent, that card takes focus. This is for controls drawn inside the card, such as its
   * menu, and focus follows only when the board's focus is on this card.
   */
  moveCard: (move: Pick<BoardCardMove, 'axis' | 'step'>) => void
}

/**
 * The moves one card can make without a pointer, and the call that makes them.
 *
 * `@github-ui/board` draws these in every card's menu. A board assembled by hand can draw
 * them however it likes.
 *
 * **Call this only while a menu is open.** It reads the board's data and config, so a
 * component calling it re-renders on every fetch and whenever the caller hands over a new
 * accessor. That is fine for an open menu and too much for every card on the board.
 *
 * The board writes nothing, as with a drop. The card stays where it is until the caller
 * hands back data that says otherwise.
 */
export function useBoardCardMoves(itemId: ItemId, cell: CellAddress): UseBoardCardMovesResult {
  const {columnKey, rowKey} = cell
  const {data, columnsByKey, rowsByKey, cellsByAddress} = useBoardDataContext()
  const {hasRowHeaders} = useBoardConfig()
  const grid = useBoardFocusGrid()
  const {dispatch} = useBoardFocusStore()
  const canMoveCards = useBoardCanMoveCards()
  const refusalFor = useBoardCardDragPolicy()
  const isHandOrdered = useBoardCellOrdering()
  const canDrop = useBoardItemDropPolicy()
  const reportItemsMove = useBoardItemsMoveReport()

  const refusal = canMoveCards ? refusalFor(composeCellKey({columnKey, rowKey}), itemId) : undefined
  const {hasMoreColumns, hasMoreRows} = data

  const options = useMemo(() => {
    if (!canMoveCards) return []

    return cardMoveOptions(
      {columnKey, rowKey, itemId},
      {
        columnKeys: grid.columnKeys,
        rowKeys: grid.rowKeys,
        hasMoreColumns,
        hasMoreRows,
        itemIdsAt: grid.itemIdsAt,
        hasUnloadedItemsAt: address => cellHasUnloadedItems(cellsByAddress.get(composeCellKey(address))),
        isHandOrdered,
        canDrop,
      },
      // A card with no id has nothing to name in a move, and the caller stated no reason.
      itemId !== '' && refusal === undefined,
    )
  }, [
    canMoveCards,
    columnKey,
    rowKey,
    itemId,
    grid,
    hasMoreColumns,
    hasMoreRows,
    cellsByAddress,
    isHandOrdered,
    canDrop,
    refusal,
  ])

  const moves = useMemo<BoardCardMove[]>(
    () =>
      options.map(({axis, step, to, move}) => {
        const isAvailable = move !== undefined
        if (to === undefined || (to.columnKey === columnKey && to.rowKey === rowKey)) return {axis, step, isAvailable}

        const columnLabel = columnsByKey.get(to.columnKey)?.label
        if (columnLabel === undefined) return {axis, step, isAvailable}

        const rowLabel = hasRowHeaders ? rowsByKey.get(to.rowKey)?.label : undefined

        return {
          axis,
          step,
          isAvailable,
          destination: rowLabel === undefined ? {columnLabel} : {columnLabel, rowLabel},
        }
      }),
    [options, columnKey, rowKey, columnsByKey, rowsByKey, hasRowHeaders],
  )

  const moveCard = useCallback(
    ({axis, step}: Pick<BoardCardMove, 'axis' | 'step'>) => {
      const move = options.find(option => option.axis === axis && option.step === step)?.move
      if (move === undefined) return

      // Armed before the report, so a caller that applies the move in the same call has it
      // land on a board that is already following the card. A reorder is followed too:
      // moving a focused card within its list drops focus to the document.
      dispatch(
        move.anchor === undefined
          ? {type: 'follow', grid, card: {columnKey, rowKey, itemId}, to: move.to}
          : {type: 'follow', grid, card: {columnKey, rowKey, itemId}, to: move.to, anchor: move.anchor},
      )

      reportItemsMove(move)
    },
    [options, grid, columnKey, rowKey, itemId, dispatch, reportItemsMove],
  )

  return useMemo(() => ({moves, refusal, moveCard}), [moves, refusal, moveCard])
}

/**
 * Whether a card in this cell has any direction to move in, so a menu that offers moves
 * has something to show. `false` on a board with no `onItemsMove`, and on a fully loaded
 * one-column, one-swimlane board in a cell the caller does not rank by hand.
 *
 * True for a card the caller refuses to move, since its menu says why. Unlike
 * {@link useBoardCardMoves} it reads no cards, so a cell can call it for every card it draws.
 */
export function useBoardCellHasCardMoves(cell: CellAddress): boolean {
  const {data} = useBoardDataContext()
  const {columnKeys, rowKeys} = useBoardFocusGrid()
  const canMoveCards = useBoardCanMoveCards()
  const isHandOrdered = useBoardCellOrdering()
  if (!canMoveCards) return false

  const {hasMoreColumns, hasMoreRows} = data

  return cardMoveAxes(cell, {columnKeys, rowKeys, hasMoreColumns, hasMoreRows, isHandOrdered}).length > 0
}
